import { StartOrConfirmAlignmentEventProperties } from "@/analytics/cad-bim-events-properties";
import { useCurrentProjectApiClient } from "@/components/common/project-provider/project-loading-context";
import { updateProject } from "@/components/common/project-provider/update-project";
import { ModalSpinner } from "@/components/ui/modal-spinner";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectActiveCadModel } from "@/store/cad/cad-slice";
import { changeMode } from "@/store/mode-slice";
import { AlignmentViewLayout } from "@/store/modes/alignment-ui-types";
import {
  selectIncrementalSheetCadTransform,
  selectSheetCadAlignmentLayout,
  selectSheetCadAlignmentReference,
  selectSheetCadAlignmentStep,
  selectSheetElevation,
} from "@/store/modes/sheet-cad-alignment-mode-selectors";
import {
  resetSheetCadAlignment,
  setClippingBoxEnabled,
  setSheetCadAlignmentLayout,
  SheetCadAlignmentStep,
  SheetCadAlignReference,
} from "@/store/modes/sheet-cad-alignment-mode-slice";
import { setActiveElement } from "@/store/selections-slice";
import {
  useAppDispatch,
  useAppSelector,
  useAppStore,
} from "@/store/store-hooks";
import { useBoxControlsContext } from "@/utils/box-controls-context";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import { grey, useToast } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  isIElementAreaSection,
  isIElementBimModelSection,
} from "@faro-lotv/ielement-types";
import {
  selectAncestor,
  selectIElementProjectApiLocalPose,
} from "@faro-lotv/project-source";
import {
  createMutationSetElementPosition,
  createMutationSetElementRotation,
  createMutationSetElementScale,
} from "@faro-lotv/service-wires";
import { Stack } from "@mui/material";
import { useCallback, useState, type JSX } from "react";
import {
  alignmentTransformToMatrix4,
  IDENTITY,
  matrix4ToAlignmentTransform,
} from "../alignment-modes-commons/alignment-transform";
import { AlignmentViewLayoutToggle } from "../alignment-modes-commons/alignment-view-layout-toggle";
import { createAreaAlignmentMutations } from "../alignment-modes-commons/project-alignment-mutations";
import { useSheetSelectedForAlignment } from "../mode-data-context";
import { SheetCadCrossSectionToggle } from "./sheet-cad-alignment-cross-section-toggle";
import { SheetCadAlignmentElevation } from "./sheet-cad-alignment-elevation";
import { SheetCadAlignmentProgressBar } from "./sheet-cad-alignment-progress-bar";
import { SheetCadAlignmentSplitScreen } from "./sheet-cad-alignment-split-screen";

/** @returns The overlay for the sheet and CAD alignment mode */
export function SheetCadAlignmentModeOverlay(): JSX.Element {
  const [showSpinner, setShowSpinner] = useState(false);
  const { openToast } = useToast();

  const store = useAppStore();
  const dispatch = useAppDispatch();
  const client = useCurrentProjectApiClient();

  const step = useAppSelector(selectSheetCadAlignmentStep);

  const alignmentLayout = useAppSelector(selectSheetCadAlignmentLayout);

  const incrementalTransform =
    useAppSelector(selectIncrementalSheetCadTransform) ?? IDENTITY;
  const sheetElevation = useAppSelector(selectSheetElevation);
  const sheet = useSheetSelectedForAlignment("sheetAndCad");
  const sectionArea = useAppSelector(
    selectAncestor(sheet, isIElementAreaSection),
  );
  if (!sectionArea) {
    throw new Error("Section Area not found for selected sheet.");
  }

  const { clippingPlanes, setClippingPlanes } = useBoxControlsContext();

  // store original clippingPlanes when entering to sheet-to-cad alignment mode and restoring original value back at return
  useNonExhaustiveEffect(() => {
    const originalClippingPlanes = clippingPlanes;
    setClippingPlanes(undefined);

    return () => {
      setClippingPlanes(originalClippingPlanes);
      dispatch(setClippingBoxEnabled(false));
    };
  }, []);

  const { handleErrorWithToast } = useErrorHandlers();

  const sectionAreaWorldMatrix = useAppSelector(
    selectIElementWorldMatrix4(sectionArea.id),
  );
  const sectionAreaWorldTransform = matrix4ToAlignmentTransform(
    sectionAreaWorldMatrix,
  );

  const sheetWorldMatrix = useAppSelector(selectIElementWorldMatrix4(sheet.id));
  const sheetWorldTransform = matrix4ToAlignmentTransform(sheetWorldMatrix);

  const activeCad = useAppSelector(selectActiveCadModel);
  assert(activeCad, "Cad model for alignment not defined");
  const cadSection = useAppSelector(
    selectAncestor(activeCad, isIElementBimModelSection),
  );
  assert(cadSection, "Parent section for cad not found");
  const cadSectionWorldMatrix = useAppSelector(
    selectIElementWorldMatrix4(cadSection.id),
  );

  const alignmentReference = useAppSelector(selectSheetCadAlignmentReference);

  const applyCadMutation = useCallback(async () => {
    Analytics.track<StartOrConfirmAlignmentEventProperties>(
      "confirmAlignment",
      {
        combination: "model-to-sheet",
      },
    );

    setShowSpinner(true);

    const transformToApply = alignmentTransformToMatrix4(
      incrementalTransform,
    ).multiply(cadSectionWorldMatrix);

    const alignmentTransform = matrix4ToAlignmentTransform(transformToApply);
    alignmentTransform.position[1] =
      matrix4ToAlignmentTransform(cadSectionWorldMatrix).position[1] +
      sheetWorldTransform.position[1] -
      sheetElevation;

    const mutationTransform = selectIElementProjectApiLocalPose(
      cadSection,
      alignmentTransformToMatrix4(alignmentTransform),
    )(store.getState());

    const mutations = [
      createMutationSetElementPosition(cadSection.id, mutationTransform.pos),
      createMutationSetElementRotation(cadSection.id, mutationTransform.rot),
      createMutationSetElementScale(cadSection.id, {
        x: 1,
        y: 1,
        z: 1,
      }),
    ];

    try {
      await client.applyMutations(mutations);

      // Fetch the changed cad section sub-tree and update the local copy of the project
      // that new alignment will be used without reloading whole project
      dispatch(
        updateProject({
          projectApi: client,
          iElementQuery: {
            // Refresh the section node to get new transform
            ancestorIds: [cadSection.id],
          },
        }),
      );

      // at the end of alignment cycle reset temporary data to prevent reusing it in the next session of alignment
      dispatch(resetSheetCadAlignment());

      setShowSpinner(false);
      openToast({
        title: "Alignment Completed",
        variant: "success",
      });
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to save new alignment",
        error,
      });

      setShowSpinner(false);
      return;
    }

    dispatch(changeMode("overview"));
  }, [
    cadSection,
    cadSectionWorldMatrix,
    client,
    dispatch,
    handleErrorWithToast,
    incrementalTransform,
    openToast,
    sheetElevation,
    sheetWorldTransform.position,
    store,
  ]);

  const applyAreaMutation = useCallback(async () => {
    Analytics.track<StartOrConfirmAlignmentEventProperties>(
      "confirmAlignment",
      {
        combination: "sheet-to-model",
      },
    );

    setShowSpinner(true);

    // from UI we've obtained incremental sheet to cad transform (only changes from current alignment session)
    // we need multiply the incremental matrix with original, to obtain absolute values in W.C.S
    const sheetToCadMatrix = alignmentTransformToMatrix4(incrementalTransform);
    const transform = matrix4ToAlignmentTransform(
      sheetToCadMatrix.multiply(sectionAreaWorldMatrix),
    );

    // We are computing the delta elevation to apply to the section area
    transform.position[1] =
      sheetElevation +
      sectionAreaWorldTransform.position[1] -
      sheetWorldTransform.position[1];

    const localTransform = selectIElementProjectApiLocalPose(
      sectionArea,
      alignmentTransformToMatrix4(transform),
    )(store.getState());

    try {
      await client.applyMutations(
        createAreaAlignmentMutations(sectionArea, {
          position: [
            localTransform.pos.x,
            localTransform.pos.y,
            localTransform.pos.z,
          ],
          quaternion: [
            localTransform.rot.x,
            localTransform.rot.y,
            localTransform.rot.z,
            localTransform.rot.w,
          ],
          scale: [
            localTransform.scale.x,
            localTransform.scale.y,
            localTransform.scale.z,
          ],
        }),
      );
    } catch (error) {
      handleErrorWithToast({
        title: "Failed to save new alignment",
        error,
      });

      setShowSpinner(false);

      return;
    }

    // Fetch the changed section area sub-tree and update the local copy of the project
    // that new alignment will be used without reloading whole project
    dispatch(
      updateProject({
        projectApi: client,
        iElementQuery: {
          // Refresh the area node to get new transform
          ancestorIds: [sectionArea.id],
        },
      }),
    );

    // at the end of alignment cycle reset temporary data to prevent reusing it in the next session of alignment
    dispatch(resetSheetCadAlignment());

    // after new alignment applied user will be redirected to 3D view in "overview" mode
    // force sheet to be new active area to immediately show results of alignment
    dispatch(setActiveElement(sheet.id));

    setShowSpinner(false);
    openToast({
      title: "Alignment completed",
      variant: "success",
    });

    // after alignment force switch to overview mode as most convenient to validate alignment result in main 3D view
    dispatch(changeMode("overview"));
  }, [
    incrementalTransform,
    sectionAreaWorldMatrix,
    sheetElevation,
    sectionAreaWorldTransform.position,
    sheetWorldTransform.position,
    sectionArea,
    store,
    dispatch,
    client,
    sheet.id,
    openToast,
    handleErrorWithToast,
  ]);

  const changeAlignmentScreenLayout = useCallback(
    (value: AlignmentViewLayout) => dispatch(setSheetCadAlignmentLayout(value)),
    [dispatch],
  );

  const alignmentViewLayout = useAppSelector(selectSheetCadAlignmentLayout);

  return (
    <>
      <ModalSpinner
        sx={{ color: grey[0], zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showSpinner}
      />
      <Stack
        sx={{
          width: "100%",
          height: "100%",
        }}
      >
        <SheetCadAlignmentProgressBar
          apply={
            alignmentReference === SheetCadAlignReference.sheet
              ? applyCadMutation
              : applyAreaMutation
          }
        />

        {step === SheetCadAlignmentStep.setElevation && (
          <SheetCadAlignmentElevation />
        )}
        {step === SheetCadAlignmentStep.alignIn2D &&
          alignmentLayout === AlignmentViewLayout.overlay && (
            <SheetCadCrossSectionToggle />
          )}
        {step === SheetCadAlignmentStep.alignIn2D && (
          <>
            {alignmentLayout === AlignmentViewLayout.splitScreen && (
              <SheetCadAlignmentSplitScreen />
            )}

            <AlignmentViewLayoutToggle
              alignmentLayout={alignmentViewLayout}
              changeAlignmentScreenLayout={changeAlignmentScreenLayout}
            />
          </>
        )}
      </Stack>
    </>
  );
}
